import { Capacitor } from "@capacitor/core";
// Static import — dynamic import() chunks never load in the Capacitor
// WKWebView (see src/lib/nativePush.ts). Guarded by isNativePlatform().
import { SocialLogin } from "@capgo/capacitor-social-login";
import { GOOGLE_CLIENT_ID } from "../config";

// The iOS OAuth client (a public identifier, like the Apple clientId in
// appleSignIn.ts). It must live in the SAME Google Cloud project as the web
// client. The web client ID doubles as iOSServerClientId: Google then issues
// the idToken with the WEB client as its audience, which is exactly what the
// server already verifies against (routes/auth.js — audience:
// process.env.GOOGLE_CLIENT_ID). No server change needed.
const GOOGLE_IOS_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID ||
  "65747247699-2kil52j0jqq666qo1rl9k5qmufeuqvv5.apps.googleusercontent.com";

let initialized = false;

async function ensureInitialized(): Promise<void> {
  if (initialized) return;
  await SocialLogin.initialize({
    google: {
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      iOSServerClientId: GOOGLE_CLIENT_ID,
      webClientId: GOOGLE_CLIENT_ID, // Android (future) + parity with web
      mode: "online",
    },
  });
  initialized = true;
}

export function isNativeGoogleConfigured(): boolean {
  return Capacitor.isNativePlatform() && !!GOOGLE_IOS_CLIENT_ID;
}

// True when the throw came from the user dismissing Google's sheet — callers
// treat that as a non-event (mirrors the Apple buttons' cancel handling).
export function isGoogleCancellation(err: unknown): boolean {
  const msg = (err as { message?: string })?.message || "";
  return /cancel/i.test(msg);
}

// Triggers native Sign in with Google and resolves with the idToken the
// server's /auth/google/* routes expect as `credential`, or null if Google
// returned no token. Throws on real failures (including cancellation —
// use isGoogleCancellation to tell those apart).
export async function signInWithGoogleNative(): Promise<string | null> {
  await ensureInitialized();
  const res = await SocialLogin.login({ provider: "google", options: {} });
  const result = res?.result as { idToken?: string | null } | undefined;
  return result?.idToken || null;
}
